import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Autocomplete, TextField, Typography, InputAdornment } from '@mui/material';
import { usePermissions, useTranslate } from 'react-admin';
import SearchIcon from '@/assets/icons/search-icon.svg?react';
import { normalize, normalizeKey } from '@/helpers/format/normalizeStrings';

export const CollectionPointMapFilters = ({ collectionPointData, selection, onSelectionChange, disabled = false }) => {
    const { permissions } = usePermissions();
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const translate = useTranslate();
    const isAdminOrBok = permissions?.isAdmin || permissions?.isBok;

    const unifiedOptions = useMemo(() => {
        const entries = [];
        const seen = new Set();

        (collectionPointData || []).forEach(collectionPoint => {
            const cityRaw = normalize(collectionPoint.city || collectionPoint.address?.city);
            const cityId = normalizeKey(cityRaw);
            if (cityId && !seen.has('city:' + cityId)) {
                seen.add('city:' + cityId);
                entries.push({ id: 'city:' + cityId, label: cityRaw, type: 'Miasto', value: cityId });
            }

            const collectionPointIdRaw = normalize(collectionPoint.customId);
            const collectionPointId = normalizeKey(collectionPointIdRaw);
            if (collectionPointId && !seen.has('collectionPoint:' + collectionPointId)) {
                seen.add('collectionPoint:' + collectionPointId);
                entries.push({ id: 'collectionPoint:' + collectionPointId, label: collectionPointIdRaw, type: 'CollectionPoint', value: collectionPointId });
            }

            if (isAdminOrBok && Array.isArray(collectionPoint.devices)) {
                collectionPoint.devices.forEach(d => {
                    const group = d?.group;
                    if (group?.id && !seen.has('group:' + group.id)) {
                        seen.add('group:' + group.id);
                        entries.push({ id: 'group:' + group.id, label: group.name || group.id, type: 'Grupa', value: group.id });
                    }
                });
            }
        });

        return entries.sort((a, b) => a.label.localeCompare(b.label));
    }, [collectionPointData, isAdminOrBok]);

    return (
        <Box display='flex' gap={2} flexWrap='wrap'>
            <Autocomplete
                size='small'
                disabled={disabled}
                options={unifiedOptions}
                getOptionLabel={opt => opt.label}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                value={selection || null}
                onChange={(e, value) => {
                    onSelectionChange(value || null);
                    setIsOpen(false);
                }}
                inputValue={inputValue}
                onInputChange={(e, newValue) => {
                    setInputValue(newValue);
                    setIsOpen(Boolean(newValue.trim()));
                }}
                open={isOpen && Boolean(inputValue.trim())}
                onOpen={() => setIsOpen(Boolean(inputValue.trim()))}
                onClose={() => setIsOpen(false)}
                openOnFocus={false}
                noOptionsText={inputValue.trim() ? translate('resources.collectionPoint.labels.noCollectionPointFound') : null}
                filterOptions={(options, state) => {
                    const q = (state.inputValue || '').trim().toLowerCase();
                    if (!q) return [];
                    return options.filter(o => o.label?.toLowerCase().includes(q));
                }}
                renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                        <Typography variant='body2'>{option.label}</Typography>
                    </li>
                )}
                renderInput={params => (
                    <TextField
                        {...params}
                        label={translate('resources.collectionPoint.labels.searchPlaceholder')}
                        fullWidth
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <InputAdornment position='end'>
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                    />
                )}
                sx={{ width: 320, maxWidth: 320, flex: 1 }}
            />
        </Box>
    );
};

CollectionPointMapFilters.propTypes = {
    collectionPointData: PropTypes.arrayOf(PropTypes.object),
    selection: PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onSelectionChange: PropTypes.func,
    disabled: PropTypes.bool,
};

export default CollectionPointMapFilters;
